import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { leadsApi } from "../api/leads";
import { getStoredUtms, metaEventId, trackEvent, trackMetaEvent } from "../lib/analytics";

// Deliberately an inline panel, not a modal: a modal would cover the page the
// visitor is reading, and browser dialogs are worse still. It renders in place
// of the CTA it replaces.
//
// Styled with Tailwind plus the global `feature-card` class rather than the
// `.tm-landing`-scoped CSS, because this renders in two different styling
// contexts (the landing, and /download which is Tailwind-only).
// `heading` and `note` are optional and only /ig passes them. The panel is
// shared with /download and the homepage, where the surrounding page already
// says what the field is for; on /ig it is the page's primary action and has
// to announce itself. The strings stay with the caller rather than moving in
// here, because "start your free trial" is /ig's promise, not this widget's.
export default function LeadCapturePanel({
  source,
  onDismiss,
  autoReveal = false,
  heading = null,
  note = null,
}) {
  const { t } = useTranslation("landing");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [sentAddress, setSentAddress] = useState("");

  // Ref guard, not trackEventOnce: StrictMode double-invokes this effect in
  // development, but lead_capture_shown must still fire once per real panel
  // open every time (the capture rate depends on an accurate shown count).
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    // `autoReveal` is only set when the panel opened from a tap, so it doubles
    // as the honest name for how the visitor met the field. Recorded because
    // /ig stopped gating the panel behind a button on 2026-09-02: without this
    // property the capture rate silently changes denominator mid-series, from
    // "chose to try" to "landed on the page".
    trackEvent("lead_capture_shown", {
      source,
      presentation: autoReveal ? "after_tap" : "inline",
    });
  }, [source, autoReveal]);

  // Bring the panel into view when it opens in response to a tap.
  //
  // This is not polish. Measured on the landing at an iPhone viewport (390x753,
  // the exact size of the first real visitor): the Buy button ends at y=688 and
  // the panel is 235px tall, so 206px of it sits below the fold and neither the
  // email field nor the submit button is on screen. That visitor tapped Buy
  // eight times in nine minutes and never submitted, because from where they
  // were looking the button did nothing.
  //
  // Gated on autoReveal because /download renders this panel as part of the
  // page rather than from a tap; scrolling there would yank the page down on
  // load. useLayoutEffect rather than useEffect so the scroll and focus run
  // before paint, which keeps them inside the click's gesture context and gives
  // iOS Safari its best chance of opening the keyboard.
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const revealedRef = useRef(false);
  useLayoutEffect(() => {
    if (!autoReveal || revealedRef.current || !rootRef.current) return;
    revealedRef.current = true;
    rootRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    // Best effort: iOS only raises the keyboard for a focus tied to a user
    // gesture, so treat this as a convenience and never as the thing that
    // makes the field reachable. The scroll above is what does that.
    inputRef.current?.focus({ preventScroll: true });
  }, [autoReveal]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setState("sending");
    // First touch wins: prefer the value persisted at landing (see
    // lib/analytics.js) and fall back to the live URL per field only when
    // nothing was ever captured for it. trackPageview runs from a mount
    // effect long before this panel can be submitted, so storage is always
    // populated by now if the visit ever carried a UTM. A present-but-empty
    // param (e.g. "?utm_source=") counts as absent, same as a missing one.
    const params = new URLSearchParams(window.location.search);
    const stored = getStoredUtms();
    const utmSource = stored.utm_source ?? (params.get("utm_source") || null);
    const utmMedium = stored.utm_medium ?? (params.get("utm_medium") || null);
    const utmCampaign = stored.utm_campaign ?? (params.get("utm_campaign") || null);
    // Meta's Lead event travels twice: once from the pixel below and once
    // from the server after the email is queued. Both copies must carry this
    // exact string or Meta, which deduplicates on (event_name, event_id),
    // counts the lead twice. It is not sent to the server: both sides derive
    // it from the address independently (see lib/analytics.js).
    const eventId = await metaEventId("lead", address);
    try {
      await leadsApi.sendResumeLink({
        email: address,
        source,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
      });
      trackEvent("lead_capture_submit", { source });
      // After the request, not before: a Lead that never reached us is not a
      // lead. The pixel copy is the fast one and the only one carrying _fbp.
      trackMetaEvent("Lead", eventId, { content_name: source });
      setSentAddress(address);
      setState("sent");
    } catch {
      setState("error");
    }
  };

  if (state === "sent") {
    return (
      <div ref={rootRef} className="feature-card rounded-lg p-5 mt-4 text-left">
        <p className="text-sm text-white">{t("leadCapture.sent", { email: sentAddress })}</p>
        {/* The most likely way to lose a mobile lead is a fat-fingered address,
            so this has to lead back to the form rather than dead-end here. The
            `email` state is left untouched by submit (only `sentAddress` is
            set), so it still holds exactly what the visitor typed: reopening
            the form keeps that value in place, ready to fix one character,
            rather than clearing it and making them retype the whole thing. */}
        <button
          type="button"
          onClick={() => setState("idle")}
          className="mt-3 text-xs text-[#6a6a6a] underline hover:text-[#A1A1AA]"
        >
          {t("leadCapture.editEmail")}
        </button>
      </div>
    );
  }

  return (
    <form ref={rootRef} className="feature-card rounded-lg p-5 mt-4 text-left" onSubmit={handleSubmit}>
      {heading && (
        <h2 className="font-clash text-xl text-white">{heading}</h2>
      )}
      <p className={`text-sm text-[#A1A1AA]${heading ? " mt-2" : ""}`}>
        {t("leadCapture.pitch")}
      </p>
      {/* Stacked on a phone, side by side from `sm` up. It used to be one
          wrapping row everywhere, which on a 375px screen put the button below
          the field at its own natural width — reading as an afterthought next
          to the input rather than as the thing to press. Full width under the
          field is the shape a primary action has on a phone. /download and the
          homepage render this panel at desktop widths, where the row still
          fits and is left alone. */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <input
          ref={inputRef}
          // `type`, `autoComplete` and `inputMode` are not enough on their own:
          // the in-app browsers (Instagram's above all) read `name` and `id`
          // too before they offer a saved address, and a field without them
          // gets no suggestion bar at all. Cheapest possible help for the one
          // step this whole page exists to get through. The id is derived from
          // `source` because TrapManLanding mounts this panel from two places.
          id={`lead-email-${source}`}
          name="email"
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={state === "sending"}
          placeholder={t("leadCapture.placeholder")}
          aria-label={t("leadCapture.placeholder")}
          className="w-full rounded border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-[#6a6a6a] focus:border-[#ff5500] focus:outline-none disabled:opacity-60 sm:w-auto sm:min-w-[200px] sm:flex-1"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="btn-primary w-full rounded px-5 py-2 text-sm font-semibold disabled:opacity-60 sm:w-auto"
        >
          {state === "sending" ? t("leadCapture.sending") : t("leadCapture.submit")}
        </button>
      </div>
      {state === "error" && (
        <p className="mt-3 text-sm text-[#ff5500]">{t("leadCapture.error")}</p>
      )}
      {/* Directly under the button on purpose: "no card needed" answers the
          hesitation at the moment it happens, which is the instant before the
          tap. It used to sit three blocks lower, below the buy button, in the
          dimmest grey on the page. */}
      {note && (
        <p className="mt-3 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
          {note}
        </p>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="mt-3 text-xs text-[#6a6a6a] underline hover:text-[#A1A1AA]"
        >
          {t("leadCapture.dismiss")}
        </button>
      )}
    </form>
  );
}
